// Typed client wrapper around Tauri's invoke().
//
// Rust commands return `AppResult<T>` which serialises as either the
// success value or an error tagged object `{ kind, message }`. We surface
// that as a Result type and offer typed command helpers so call sites
// never write `invoke('guardian_create', ...)` as a raw string.

import { invoke } from '@tauri-apps/api/core';

export type Ok<T> = { ok: true; value: T };
export type Err<E> = { ok: false; error: E };
export type Result<T, E> = Ok<T> | Err<E>;

export function ok<T>(value: T): Ok<T> {
  return { ok: true, value };
}

export function err<E>(error: E): Err<E> {
  return { ok: false, error };
}

export type AppErrorKind =
  | 'validation'
  | 'not_found'
  | 'unauthorized'
  | 'conflict'
  | 'internal'
  | 'network_required';

export interface AppError {
  kind: AppErrorKind;
  message: string;
}

function toAppError(unknown: unknown): AppError {
  // Tauri serialises our AppError enum as `{ kind, message }`. Anything
  // else (a thrown JS error, a plugin error, etc.) is treated as internal.
  if (
    typeof unknown === 'object' &&
    unknown !== null &&
    'kind' in unknown &&
    'message' in unknown
  ) {
    const maybe = unknown as { kind: unknown; message: unknown };
    if (typeof maybe.kind === 'string' && typeof maybe.message === 'string') {
      return { kind: maybe.kind as AppErrorKind, message: maybe.message };
    }
  }
  const message = unknown instanceof Error ? unknown.message : String(unknown);
  return { kind: 'internal', message };
}

// ---- Command inputs/outputs (mirror Rust types) ----

export interface GuardianCreateInput {
  displayName: string;
  password: string;
  displayCurrencyCode: string;
  familyName?: string;
  acceptedFamilyContentPolicyVersion: string;
}

export interface FamilyContentPolicyStatusResult {
  roleCode: 'guardian' | 'child';
  familyTermsVersion: string;
  familyTermsAccepted: boolean;
  childRulesVersion: string;
  childRulesAccepted: boolean;
}

export interface FamilyContentReporterContext {
  accountId: number;
  roleCode: 'guardian' | 'child';
  displayName: string;
}

export interface GuardianCreateResult {
  accountId: number;
  displayName: string;
}

export interface AccountListEntry {
  accountId: number;
  displayName: string;
  roleCode: 'guardian' | 'child';
  displayCurrencyCode: string;
  displayAvatar: string | null;
}

export interface AuthLoginInput {
  accountId: number;
  password: string;
}

/**
 * Result of `auth_login`. Discriminated by `outcome`:
 *   - `success` -- session created, login complete
 *   - `locked`  -- account is locked out (US-AUTH-03 / REQ-9.6); the UI
 *                 should show a "try again in N minutes" message and
 *                 disable the form for `retryAfterSeconds` seconds.
 *
 * Note: wrong-password / missing-account / inactive-account cases are
 * thrown as `AppError.unauthorized` -- they are NOT a `locked` outcome.
 */
export type AuthLoginResult =
  | {
      outcome: 'success';
      sessionId: string;
      accountId: number;
      /** ISO 8601 UTC timestamp. */
      expiresAtUtc: string;
    }
  | {
      outcome: 'locked';
      retryAfterSeconds: number;
      /** ISO 8601 UTC timestamp; redundant with retryAfterSeconds. */
      unlocksAtUtc: string;
    };

export interface AuthLogoutInput {
  sessionId: string;
}

export interface AuthLogoutResult {
  /** True if a session row was deleted; false if it was already gone. */
  sessionWasActive: boolean;
}

export interface FamilyDataDeleteLocalInput {
  sessionId: string;
}

export interface FamilyDataDeleteLocalOutput {
  deleted: boolean;
}

/** Completes a fresh-device cloud restore by creating this device's local
 * guardian password. Provider credentials are used only for this one-time
 * restore proof and are never saved. */
export interface CloudRestoreCompleteInput {
  localAccountId: number;
  accessToken: string;
  publishableKey: string;
  supabaseUrl: string;
  familyId: string;
  familyAccountId: string;
  newPassword: string;
}

export interface CloudRestoreCompleteResult {
  outcome: 'success';
  sessionId: string;
  accountId: number;
  expiresAtUtc: string;
}

export interface CloudSubscriptionLinkInput {
  sessionId: string;
  localAccountId: number;
  accessToken: string;
  publishableKey: string;
  supabaseUrl: string;
  familyId: string;
  familyAccountId: string;
}

export interface CloudSyncRow {
  sourceTable: string;
  sourceOrdinal: number;
  sourceRowJson: string;
  sourceRowSha256: string;
}

export interface CloudSyncFamilyInput {
  accessToken: string;
  publishableKey: string;
  supabaseUrl: string;
  familyId: string;
  familyAccountId: string;
  importId: string;
  rows: CloudSyncRow[];
}

export interface CloudSyncFamilyResult {
  localAccountId: number;
  displayName: string;
  importedRows: number;
}

export interface SessionTouchInput {
  sessionId: string;
}

/**
 * Result of `session_touch`. Discriminated by `outcome`:
 *   - `alive`   -- session still valid; new expiresAtUtc is the fresh idle deadline
 *   - `expired` -- session gone; frontend should route to account selection
 */
export type SessionTouchResult =
  | { outcome: 'alive'; expiresAtUtc: string }
  | { outcome: 'expired' };

export interface LoginSecurityAlert {
  loginSecurityAlertId: number;
  targetAccountId: number;
  targetDisplayName: string;
  targetRoleCode: 'guardian' | 'child';
  credentialKind: 'password' | 'pin';
  lockedAtUtc: string;
  lockoutExpiresAtUtc: string;
}

export interface FamilyAccountPasswordResetInput {
  sessionId: string;
  settingsToken: string;
  loginSecurityAlertId: number;
  newPassword: string;
}

export interface FamilyAccountPasswordResetResult {
  accountId: number;
}

export interface ChildCreateInput {
  sessionId: string;
  displayName: string;
  password: string;
  displayCurrencyCode: string;
  /** Defaults to "parent" if omitted. */
  guardianTypeCode?: string;
}

export interface ChildCreateResult {
  accountId: number;
  displayName: string;
}

export interface ChildPasswordResetInput {
  sessionId: string;
  childAccountId: number;
  newPassword: string;
}

export interface ChildPasswordResetResult {
  childAccountId: number;
}

export interface ChildDeactivateInput {
  sessionId: string;
  childAccountId: number;
}

export interface ChildDeactivateResult {
  childAccountId: number;
  deletedAtUtc: string;
  purgeAfterUtc: string;
}

export interface RecoverableChildEntry {
  childAccountId: number;
  displayName: string;
  deletedAtUtc: string;
  purgeAfterUtc: string;
}

export interface ChildRestoreInput {
  sessionId: string;
  childAccountId: number;
  displayName: string;
  newPassword: string;
}

export interface ChildRestoreResult {
  childAccountId: number;
  displayName: string;
}

export interface GuardianTypeEntry {
  typeCode: string;
  typeLabel: string;
  sortOrder: number;
}

export interface ChildGuardianTypeUpdateInput {
  sessionId: string;
  childAccountId: number;
  guardianTypeCode: string;
}

export interface ChildGuardianTypeUpdateResult {
  childAccountId: number;
  guardianTypeCode: string;
}

export interface ChoreTypeEntry {
  typeCode: string;
  typeLabel: string;
  sortOrder: number;
}

export interface ChoreCreateInput {
  sessionId: string;
  /** US-CHORE-07: null/undefined = master-list ("any child" chore);
   *  number = chore assigned to a specific child. */
  childAccountId?: number | null;
  choreName: string;
  choreDescription?: string;
  /** One of: zone, a_la_carte, recurring. */
  choreTypeCode: string;
  zoneName?: string;
  rewardMinorUnits: number;
  recurrencePattern?: string;
  /** US-CHORE-07: only meaningful for master-list chores
   *  (childAccountId nullish). When true, picking the chore creates
   *  a pending claim that the guardian must approve. Backend rejects
   *  with Validation if set on an assigned chore. */
  requiresApprovalToClaim?: boolean;
}

export interface ChoreEntry {
  choreId: number;
  /** US-CHORE-07: null for master-list chores ("any child"). */
  childAccountId: number | null;
  /** Present for assigned chores so family-wide guardian lists can identify the child. */
  childDisplayName?: string | null;
  /** US-CHORE-07: the guardian who created the chore. Used by the
   *  redesigned guardian filter view. */
  createdByAccountId: number;
  choreName: string;
  choreDescription: string | null;
  choreTypeCode: string;
  choreTypeLabel: string;
  zoneName: string | null;
  rewardMinorUnits: number;
  recurrencePattern: string | null;
  isActive: boolean;
  /** US-CHORE-07: only meaningful when childAccountId is null. */
  requiresApprovalToClaim: boolean;
  createdAtUtc: string;
  /** US-CHORE-02: true when the child has marked this chore done and is awaiting guardian approval. */
  hasOpenClaim: boolean;
  /** US-UI-CHORE-REJECTIONS: present iff the most recent completion
   *  for this chore has status `rejected`. The Chores screen renders
   *  a red row + the reason inline when these are non-null. Cleared
   *  implicitly by a subsequent re-claim. */
  lastRejectedAtUtc: string | null;
  lastRejectionReason: string | null;
  /** US-CHORE-10 (10b): the rejected completion's id, so the child UI
   *  can call `choreCompletionResubmit` directly. Set iff
   *  `lastRejectedAtUtc` is set. */
  lastRejectedCompletionId: number | null;
}

export interface ChoresListInput {
  sessionId: string;
  /** Required for guardians targeting a specific child. Omit for the All Chores view. */
  childAccountId?: number | undefined;
}

export interface ChoreClaimInput {
  sessionId: string;
  choreId: number;
}

export interface ChoreClaimResult {
  choreCompletionId: number;
  choreId: number;
  claimedAtUtc: string;
}

export interface PendingChoreCompletionEntry {
  choreCompletionId: number;
  choreId: number;
  childAccountId: number;
  childDisplayName: string;
  choreName: string;
  choreDescription: string | null;
  choreTypeLabel: string;
  rewardMinorUnits: number;
  claimedAtUtc: string;
  /** US-CHORE-10 (10a): null = non-recurring (Reject+remove allowed),
   *  set = recurring (button hidden). */
  recurrencePattern: string | null;
}

export interface ChoreCompletionApproveInput {
  sessionId: string;
  choreCompletionId: number;
  /** US-CHORE-04 -- optional override for the chore's default reward at
   *  approval time. When omitted or null, the chore's default reward
   *  is used unchanged. When set to a non-negative integer, that value
   *  drives both the deposit amount and the persisted
   *  `adjusted_reward_minor_units` column on the completion. Negative
   *  values return a Validation error from the backend. */
  adjustedRewardMinorUnits?: number | null;
}

export interface ChoreAllocationEntry {
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
}

export interface ChoreCompletionApproveResult {
  choreCompletionId: number;
  rewardMinorUnits: number;
  /** Null when the chore had a zero reward (accountability-only chore). */
  depositId: number | null;
  allocations: ChoreAllocationEntry[];
}

export interface ChoreCompletionRejectInput {
  sessionId: string;
  choreCompletionId: number;
  /** Required for chore rejection (US-CHORE-05 acceptance criteria). */
  reason: string;
  /** US-CHORE-10 (10a): when true, the underlying chore is also
   *  deactivated (`chores.is_active = 0`) in the same logical step.
   *  The backend validates that the chore is non-recurring; recurring
   *  chores can't be removed at rejection. Defaults to false. */
  removeChore?: boolean;
}

export interface ChoreCompletionRejectResult {
  choreCompletionId: number;
  /** Echoes whether the underlying chore was deactivated. */
  choreRemoved: boolean;
}

// -------- US-CHORE-10 (10b/10c) -- child resubmit + archive --------

export interface ChoreCompletionResubmitInput {
  sessionId: string;
  choreCompletionId: number;
}

export interface ChoreCompletionResubmitResult {
  choreCompletionId: number;
}

export interface ChoreCompletionArchiveInput {
  sessionId: string;
  choreCompletionId: number;
}

export interface ChoreCompletionArchiveResult {
  choreCompletionId: number;
}

export interface ChoresListRejectedRemovedInput {
  sessionId: string;
}

export interface RejectedRemovedChoreEntry {
  choreCompletionId: number;
  choreId: number;
  choreName: string;
  decidedAtUtc: string;
  decisionReason: string | null;
}

// -------- US-CHORE-06 -- deactivate / reactivate / list deactivated --------

export interface ChoreDeactivateInput {
  sessionId: string;
  choreId: number;
}

export interface ChoreDeactivateResult {
  choreId: number;
}

export interface ChoreReactivateInput {
  sessionId: string;
  choreId: number;
}

export interface ChoreReactivateResult {
  choreId: number;
}

export interface InactiveChoresListInput {
  sessionId: string;
  /** Omit for the guardian's aggregated All Chores screen. */
  childAccountId: number | undefined;
}

export interface DepositCreateInput {
  sessionId: string;
  childAccountId: number;
  /** Positive integer amount in family-currency minor units, or source minor
   * units when `fx` is present. */
  amountMinorUnits: number;
  /** One of: chore, allowance, gift, found, other. */
  sourceCode: string;
  note?: string;
  /** Present only for an overseas family gift recorded in a currency other
   * than the family's GBP or USD ledger. */
  fx?: {
    currencyCode: string;
    /** Source currency -> family currency in parts-per-million. */
    ratePpm: number;
  };
}

export interface DepositAllocationEntry {
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
}

export interface DepositCreateResult {
  depositId: number;
  amountMinorUnits: number;
  allocations: DepositAllocationEntry[];
}

export interface DepositSubmitPendingInput {
  sessionId: string;
  amountMinorUnits: number;
  sourceCode: string;
  note?: string;
}

export interface DepositSubmitPendingResult {
  depositId: number;
  amountMinorUnits: number;
}

export interface PendingDepositEntry {
  depositId: number;
  childAccountId: number;
  childDisplayName: string;
  amountMinorUnits: number;
  sourceCode: string;
  sourceLabel: string;
  note: string | null;
  requestedAtUtc: string;
}

/** US-DEPOSIT-06 -- child's own pending deposits (no childDisplayName since they know who they are). */
export interface MyPendingDepositEntry {
  depositId: number;
  amountMinorUnits: number;
  sourceCode: string;
  sourceLabel: string;
  note: string | null;
  requestedAtUtc: string;
}

export interface DepositCancelInput {
  sessionId: string;
  depositId: number;
}

export interface DepositCancelResult {
  depositId: number;
}

export interface DepositApproveInput {
  sessionId: string;
  depositId: number;
}

export interface DepositApproveResult {
  depositId: number;
  amountMinorUnits: number;
  allocations: DepositAllocationEntry[];
}

export interface DepositRejectInput {
  sessionId: string;
  depositId: number;
  /** Required for deposit rejection (US-DEPOSIT-05 acceptance criteria). */
  reason: string;
}

export interface DepositRejectResult {
  depositId: number;
}

export interface WithdrawalCreateInput {
  sessionId: string;
  bucketId: number;
  amountMinorUnits: number;
  /** One of: toy, food_snack, video_game, book_media, clothing, gift, experience, charitable_giving, other. Bucket-to-bucket changes use the separate transfer API. */
  reasonCode: string;
  reasonNote?: string;
  /** Optional immutable foreign-currency source snapshot. The debit remains
   * `amountMinorUnits` in the GBP or USD family ledger. */
  fx?: WithdrawalFxSnapshotInput;
}

export interface WithdrawalFxSnapshotInput {
  sourceCurrencyCode: string;
  sourceAmountMinorUnits: number;
  /** Source currency -> the family's GBP or USD currency, stored as integer parts-per-million.
   * The legacy wire name is retained for stored-record compatibility. */
  sourceToGbpRatePpm: number;
  /** Provider business date in YYYY-MM-DD. */
  effectiveDate: string;
  provider: 'frankfurter' | 'manual';
}

export interface WithdrawalCreateResult {
  withdrawalId: number;
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
  newBalanceMinorUnits: number;
}

export interface WithdrawalSubmitPendingInput {
  sessionId: string;
  bucketId: number;
  amountMinorUnits: number;
  /** Spend reason only; bucket-to-bucket changes use the separate transfer API. */
  reasonCode: string;
  reasonNote?: string;
  fx?: WithdrawalFxSnapshotInput;
}

export interface WithdrawalSubmitPendingResult {
  withdrawalId: number;
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
}

export interface PendingWithdrawalEntry {
  withdrawalId: number;
  childAccountId: number;
  childDisplayName: string;
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
  reasonCode: string;
  reasonLabel: string;
  reasonNote: string | null;
  requestedAtUtc: string;
}

export interface WithdrawalApproveInput {
  sessionId: string;
  withdrawalId: number;
}

export interface WithdrawalApproveResult {
  withdrawalId: number;
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
  newBalanceMinorUnits: number;
}

export interface WithdrawalRejectInput {
  sessionId: string;
  withdrawalId: number;
  /** Required for withdrawal rejection (US-WITHDRAWAL-03 acceptance criteria). */
  reason: string;
}

export interface WithdrawalRejectResult {
  withdrawalId: number;
}

/** US-WITHDRAWAL-04 -- child's own pending withdrawals (no childDisplayName). */
export interface MyPendingWithdrawalEntry {
  withdrawalId: number;
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
  reasonCode: string;
  reasonLabel: string;
  reasonNote: string | null;
  requestedAtUtc: string;
}

export interface WithdrawalCancelInput {
  sessionId: string;
  withdrawalId: number;
}

export interface WithdrawalCancelResult {
  withdrawalId: number;
}

export interface HistoryListInput {
  sessionId: string;
  /** Required for guardians; children see their own history regardless. */
  childAccountId?: number;
  limit?: number;
  /** US-HISTORY-04 -- optional filter fields. All omitted = no filter. */
  atFromUtc?: string;
  atToUtc?: string;
  /** 'deposit', 'withdrawal', or 'transfer'. Omitted matches every transaction type. */
  kindFilter?: string;
  bucketIdFilter?: number;
}

export interface HistoryDepositAllocation {
  bucketId: number;
  bucketName: string;
  amountMinorUnits: number;
}

/** Terminal deposit, withdrawal, and transfer events in a unified list.
 * Discriminated by `kind`. Rejected/cancelled events retain their status and
 * reason so the child can understand the outcome in their own history. */
export type HistoryEntry =
  | {
      kind: 'deposit';
      depositId: number;
      atUtc: string;
      amountMinorUnits: number;
      sourceCode: string;
      sourceLabel: string;
      note: string | null;
      allocations: HistoryDepositAllocation[];
      statusCode: string;
      statusLabel: string;
      decisionReason: string | null;
      /** US-DEPOSIT-07: name of the guardian who approved this deposit. */
      decidedByDisplayName: string | null;
    }
  | {
      kind: 'withdrawal';
      withdrawalId: number;
      atUtc: string;
      amountMinorUnits: number;
      bucketId: number;
      bucketName: string;
      reasonCode: string;
      reasonLabel: string;
      reasonNote: string | null;
      statusCode: string;
      statusLabel: string;
      decisionReason: string | null;
      /** US-DEPOSIT-07: decider name. For self-service withdrawals this is
       *  the child themselves; for guardian-decided withdrawals it's the
       *  approving guardian. UI should suppress display when equal to
       *  the child being viewed (it's noise -- the child knows what they
       *  did themselves). */
      decidedByDisplayName: string | null;
      /** US-WITHDRAWAL-05: true if a guardian has marked this posted
       *  withdrawal as reconciled with real-world cash. UI shows a
       *  toggle button for guardians; children should not see it. */
      reconciled: boolean;
    }
  | {
      kind: 'transfer';
      transferId: number;
      /** `child_request` needs guardian approval; `guardian_direct` posts immediately. */
      transferMode: 'child_request' | 'guardian_direct';
      atUtc: string;
      amountMinorUnits: number;
      sourceBucketId: number;
      sourceBucketName: string;
      targetBucketId: number | null;
      targetBucketName: string | null;
      note: string | null;
      statusCode: string;
      statusLabel: string;
      decisionReason: string | null;
      decidedByDisplayName: string | null;
    };

export interface BucketsListInput {
  sessionId: string;
}

export interface BucketEntry {
  bucketId: number;
  childAccountId: number;
  childDisplayName: string;
  bucketName: string;
  allocationBps: number;
  isGuardianManaged: boolean;
  requiresApprovalToWithdraw: boolean;
  percentIsFixed: boolean;
  balanceMinorUnits: number;
  displayColor: string | null;
  sortOrder: number;
  /** Rounding priority 1–10. Higher = receives the ceiling value on uneven splits. */
  priority: number;
  // US-INTEREST-01 -- included so the edit form can populate from current state.
  interestEnabled: boolean;
  interestRateBps: number;
  interestPeriodCode: InterestPeriodCode | null;
  interestActuallyCredits: boolean;
}

export interface BucketAddInput {
  sessionId: string;
  childAccountId: number;
  bucketName: string;
  displayColor: string | null;
  allocationBps: number;
  isGuardianManaged: boolean;
  requiresApprovalToWithdraw: boolean;
  percentIsFixed: boolean;
}

export interface BucketRebalanceEntry {
  bucketId: number;
  newAllocationBps: number;
}

export interface BucketAddOutput {
  newBucketId: number;
  /**
   * Flexible buckets whose allocation changed as a result of making room
   * for the new bucket. Fixed buckets are never in this list -- they don't
   * change. See US-BUCKET-03.
   */
  flexibleRebalance: BucketRebalanceEntry[];
}

/**
 * US-BUCKET-04 -- edit a guardian-managed bucket.
 *
 * All mutation fields are optional; absent (undefined) fields are left
 * unchanged on the server. For `displayColor`, three-valued semantics:
 *   - `"#rrggbb"` → set colour
 *   - `null` → clear colour
 *   - `undefined` → leave unchanged
 */
export interface BucketUpdateInput {
  sessionId: string;
  bucketId: number;
  bucketName?: string;
  displayColor?: string | null;
  requiresApprovalToWithdraw?: boolean;
  allocationBps?: number;
  percentIsFixed?: boolean;
  /** Rounding priority. Higher = receives ceiling on uneven splits. */
  priority?: number;
  /**
   * BUG-024 / US-BUCKET-13 -- guardians can flip a bucket's
   * is_guardian_managed flag. Children submitting this field are
   * rejected at the db layer (manager-only flag).\
   */
  isGuardianManaged?: boolean;
  // US-INTEREST-01 -- guardian-only interest config. All four are
  // optional; absent means leave-as-is, present means apply.
  // `interestPeriodCode`: a string sets it, null clears it (sets to NULL),
  // absent leaves unchanged.
  interestEnabled?: boolean;
  interestRateBps?: number;
  interestPeriodCode?: InterestPeriodCode | null;
  interestActuallyCredits?: boolean;
}

/** US-INTEREST-01 -- accrual cadence. Mirrors the `interest_periods`
 *  lookup table seeded in migration 0001. */
export type InterestPeriodCode = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface BucketUpdateOutput {
  bucketId: number;
  /** OTHER flexible buckets whose allocation changed. */
  flexibleRebalance: BucketRebalanceEntry[];
}

// ---- Typed command helpers ----

/** Returns the migration failure message if startup failed, or null if clean. */
export async function getStartupError(): Promise<string | null> {
  try {
    return await invoke<string | null>('get_startup_error');
  } catch {
    return null;
  }
}

export async function accountsExist(): Promise<Result<boolean, AppError>> {
  try {
    const value = await invoke<boolean>('accounts_exist');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

/**
 * Reads the native shell OS when available. This is deliberately a best-effort
 * call: web and browser tests fall back to user-agent detection.
 */
export async function runtimePlatform(): Promise<Result<string, AppError>> {
  try {
    return ok(await invoke<string>('runtime_platform'));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function guardianCreate(
  input: GuardianCreateInput,
): Promise<Result<GuardianCreateResult, AppError>> {
  try {
    const value = await invoke<GuardianCreateResult>('guardian_create', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function familyContentPolicyStatus(
  sessionId: string,
): Promise<Result<FamilyContentPolicyStatusResult, AppError>> {
  try {
    return ok(
      await invoke<FamilyContentPolicyStatusResult>('family_content_policy_status', {
        input: { sessionId },
      }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function familyContentPolicyAccept(input: {
  sessionId: string;
  policyKind: 'family_terms' | 'child_rules';
  policyVersion: string;
}): Promise<Result<void, AppError>> {
  try {
    return ok(await invoke<void>('family_content_policy_accept', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function familyContentReporterContext(
  sessionId: string,
): Promise<Result<FamilyContentReporterContext, AppError>> {
  try {
    return ok(
      await invoke<FamilyContentReporterContext>('family_content_reporter_context', {
        input: { sessionId },
      }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function accountsList(): Promise<Result<AccountListEntry[], AppError>> {
  try {
    const value = await invoke<AccountListEntry[]>('accounts_list');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function authLogin(input: AuthLoginInput): Promise<Result<AuthLoginResult, AppError>> {
  try {
    const value = await invoke<AuthLoginResult>('auth_login', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function authLogout(
  input: AuthLogoutInput,
): Promise<Result<AuthLogoutResult, AppError>> {
  try {
    const value = await invoke<AuthLogoutResult>('auth_logout', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function familyDataDeleteLocal(
  input: FamilyDataDeleteLocalInput,
): Promise<Result<FamilyDataDeleteLocalOutput, AppError>> {
  try {
    return ok(await invoke<FamilyDataDeleteLocalOutput>('family_data_delete_local', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function cloudRestoreComplete(
  input: CloudRestoreCompleteInput,
): Promise<Result<CloudRestoreCompleteResult, AppError>> {
  try {
    const value = await invoke<CloudRestoreCompleteResult>('cloud_restore_complete', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function cloudSubscriptionLinkExisting(
  input: CloudSubscriptionLinkInput,
): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('cloud_subscription_link_existing', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function cloudSyncFamily(
  input: CloudSyncFamilyInput,
): Promise<Result<CloudSyncFamilyResult, AppError>> {
  try {
    const value = await invoke<CloudSyncFamilyResult>('cloud_sync_apply', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function sessionTouch(
  input: SessionTouchInput,
): Promise<Result<SessionTouchResult, AppError>> {
  try {
    const value = await invoke<SessionTouchResult>('session_touch', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function loginSecurityAlertsList(input: {
  sessionId: string;
}): Promise<Result<LoginSecurityAlert[], AppError>> {
  try {
    return ok(await invoke<LoginSecurityAlert[]>('login_security_alerts_list', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function loginSecurityAlertIgnore(input: {
  sessionId: string;
  loginSecurityAlertId: number;
}): Promise<Result<void, AppError>> {
  try {
    return ok(await invoke<void>('login_security_alert_ignore', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function familyAccountPasswordReset(
  input: FamilyAccountPasswordResetInput,
): Promise<Result<FamilyAccountPasswordResetResult, AppError>> {
  try {
    return ok(
      await invoke<FamilyAccountPasswordResetResult>('family_account_password_reset', { input }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function childCreate(
  input: ChildCreateInput,
): Promise<Result<ChildCreateResult, AppError>> {
  try {
    const value = await invoke<ChildCreateResult>('child_create', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function childPasswordReset(
  input: ChildPasswordResetInput,
): Promise<Result<ChildPasswordResetResult, AppError>> {
  try {
    const value = await invoke<ChildPasswordResetResult>('child_password_reset', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function childDeactivate(
  input: ChildDeactivateInput,
): Promise<Result<ChildDeactivateResult, AppError>> {
  try {
    const value = await invoke<ChildDeactivateResult>('child_deactivate', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function childRecoveryList(input: { sessionId: string }): Promise<
  Result<RecoverableChildEntry[], AppError>
> {
  try {
    const value = await invoke<RecoverableChildEntry[]>('child_recovery_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function childRestore(
  input: ChildRestoreInput,
): Promise<Result<ChildRestoreResult, AppError>> {
  try {
    const value = await invoke<ChildRestoreResult>('child_restore', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-SETUP-05 -- change the display currency on an account. REQ-1.7
// -- currency is metadata; balances and historical amounts stay in
// their stored minor units. Guardian-only at the backend.
export interface AccountCurrencyUpdateInput {
  sessionId: string;
  /** The account whose display currency is being changed. Caller must
   *  be a guardian and must either own this account or be linked to
   *  the child whose account it is. */
  accountId: number;
  /** ISO-4217 currency code (e.g. "GBP", "USD", "EUR"). Backend
   *  validates against the seeded currencies table. */
  newCurrencyCode: string;
}

export async function accountCurrencyUpdate(
  input: AccountCurrencyUpdateInput,
): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('account_currency_update', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface FamilyCurrencyUpdateInput {
  sessionId: string;
  newCurrencyCode: 'GBP' | 'USD';
}

export interface FamilyCurrencyUpdateResult {
  previousCurrencyCode: 'GBP' | 'USD';
  newCurrencyCode: 'GBP' | 'USD';
  accountCount: number;
}

export async function familyCurrencyUpdate(
  input: FamilyCurrencyUpdateInput,
): Promise<Result<FamilyCurrencyUpdateResult, AppError>> {
  try {
    return ok(await invoke<FamilyCurrencyUpdateResult>('family_currency_update', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function guardianTypesList(): Promise<Result<GuardianTypeEntry[], AppError>> {
  try {
    const value = await invoke<GuardianTypeEntry[]>('guardian_types_list');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function childGuardianTypeUpdate(
  input: ChildGuardianTypeUpdateInput,
): Promise<Result<ChildGuardianTypeUpdateResult, AppError>> {
  try {
    const value = await invoke<ChildGuardianTypeUpdateResult>('child_guardian_type_update', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreTypesList(): Promise<Result<ChoreTypeEntry[], AppError>> {
  try {
    const value = await invoke<ChoreTypeEntry[]>('chore_types_list');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreCreate(input: ChoreCreateInput): Promise<Result<ChoreEntry, AppError>> {
  try {
    const value = await invoke<ChoreEntry>('chore_create', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export type ChoreUpdateInput = {
  sessionId: string;
  choreId: number;
  choreName: string;
  choreDescription: string | null;
  zoneName: string | null;
  rewardMinorUnits: number;
};

export async function choreUpdate(input: ChoreUpdateInput): Promise<Result<ChoreEntry, AppError>> {
  try {
    const value = await invoke<ChoreEntry>('chore_update', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choresList(input: ChoresListInput): Promise<Result<ChoreEntry[], AppError>> {
  try {
    const value = await invoke<ChoreEntry[]>('chores_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-UI-CHORE-REJECTIONS -- child Home reads a single int for the
// red badge on My chores. Guardians get 0 (the badge is child-only).
export interface ChoresRejectedCountInput {
  sessionId: string;
}

export async function choresRejectedCount(
  input: ChoresRejectedCountInput,
): Promise<Result<number, AppError>> {
  try {
    const value = await invoke<number>('chores_rejected_count', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function guardianAllChoresCount(input: { sessionId: string }): Promise<
  Result<number, AppError>
> {
  try {
    const value = await invoke<number>('guardian_all_chores_count', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreClaim(
  input: ChoreClaimInput,
): Promise<Result<ChoreClaimResult, AppError>> {
  try {
    const value = await invoke<ChoreClaimResult>('chore_claim', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function pendingChoresList(input: { sessionId: string }): Promise<
  Result<PendingChoreCompletionEntry[], AppError>
> {
  try {
    const value = await invoke<PendingChoreCompletionEntry[]>('pending_chores_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreCompletionApprove(
  input: ChoreCompletionApproveInput,
): Promise<Result<ChoreCompletionApproveResult, AppError>> {
  try {
    const value = await invoke<ChoreCompletionApproveResult>('chore_completion_approve', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreCompletionReject(
  input: ChoreCompletionRejectInput,
): Promise<Result<ChoreCompletionRejectResult, AppError>> {
  try {
    const value = await invoke<ChoreCompletionRejectResult>('chore_completion_reject', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-CHORE-10 (10b) -- child re-submits a rejected chore completion.
export async function choreCompletionResubmit(
  input: ChoreCompletionResubmitInput,
): Promise<Result<ChoreCompletionResubmitResult, AppError>> {
  try {
    const value = await invoke<ChoreCompletionResubmitResult>('chore_completion_resubmit', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-CHORE-10 (10c) -- child archives a rejected-and-removed completion.
export async function choreCompletionArchive(
  input: ChoreCompletionArchiveInput,
): Promise<Result<ChoreCompletionArchiveResult, AppError>> {
  try {
    const value = await invoke<ChoreCompletionArchiveResult>('chore_completion_archive', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-CHORE-10 (10c) -- list rejected completions whose chore the
// guardian removed via reject-and-remove. Drives the child's archive
// affordance.
export async function choresListRejectedRemoved(
  input: ChoresListRejectedRemovedInput,
): Promise<Result<RejectedRemovedChoreEntry[], AppError>> {
  try {
    const value = await invoke<RejectedRemovedChoreEntry[]>('chores_list_rejected_removed', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreDeactivate(
  input: ChoreDeactivateInput,
): Promise<Result<ChoreDeactivateResult, AppError>> {
  try {
    const value = await invoke<ChoreDeactivateResult>('chore_deactivate', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreReactivate(
  input: ChoreReactivateInput,
): Promise<Result<ChoreReactivateResult, AppError>> {
  try {
    const value = await invoke<ChoreReactivateResult>('chore_reactivate', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function inactiveChoresList(
  input: InactiveChoresListInput,
): Promise<Result<ChoreEntry[], AppError>> {
  try {
    const value = await invoke<ChoreEntry[]>('inactive_chores_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function depositCreate(
  input: DepositCreateInput,
): Promise<Result<DepositCreateResult, AppError>> {
  try {
    const value = await invoke<DepositCreateResult>('deposit_create', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function depositSubmitPending(
  input: DepositSubmitPendingInput,
): Promise<Result<DepositSubmitPendingResult, AppError>> {
  try {
    const value = await invoke<DepositSubmitPendingResult>('deposit_submit_pending', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function pendingDepositsList(input: { sessionId: string }): Promise<
  Result<PendingDepositEntry[], AppError>
> {
  try {
    const value = await invoke<PendingDepositEntry[]>('pending_deposits_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function depositApprove(
  input: DepositApproveInput,
): Promise<Result<DepositApproveResult, AppError>> {
  try {
    const value = await invoke<DepositApproveResult>('deposit_approve', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function depositReject(
  input: DepositRejectInput,
): Promise<Result<DepositRejectResult, AppError>> {
  try {
    const value = await invoke<DepositRejectResult>('deposit_reject', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function myPendingDepositsList(input: { sessionId: string }): Promise<
  Result<MyPendingDepositEntry[], AppError>
> {
  try {
    const value = await invoke<MyPendingDepositEntry[]>('my_pending_deposits_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function depositCancel(
  input: DepositCancelInput,
): Promise<Result<DepositCancelResult, AppError>> {
  try {
    const value = await invoke<DepositCancelResult>('deposit_cancel', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function withdrawalCreate(
  input: WithdrawalCreateInput,
): Promise<Result<WithdrawalCreateResult, AppError>> {
  try {
    const value = await invoke<WithdrawalCreateResult>('withdrawal_create', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function withdrawalSubmitPending(
  input: WithdrawalSubmitPendingInput,
): Promise<Result<WithdrawalSubmitPendingResult, AppError>> {
  try {
    const value = await invoke<WithdrawalSubmitPendingResult>('withdrawal_submit_pending', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function pendingWithdrawalsList(input: { sessionId: string }): Promise<
  Result<PendingWithdrawalEntry[], AppError>
> {
  try {
    const value = await invoke<PendingWithdrawalEntry[]>('pending_withdrawals_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function withdrawalApprove(
  input: WithdrawalApproveInput,
): Promise<Result<WithdrawalApproveResult, AppError>> {
  try {
    const value = await invoke<WithdrawalApproveResult>('withdrawal_approve', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function withdrawalReject(
  input: WithdrawalRejectInput,
): Promise<Result<WithdrawalRejectResult, AppError>> {
  try {
    const value = await invoke<WithdrawalRejectResult>('withdrawal_reject', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function myPendingWithdrawalsList(input: { sessionId: string }): Promise<
  Result<MyPendingWithdrawalEntry[], AppError>
> {
  try {
    const value = await invoke<MyPendingWithdrawalEntry[]>('my_pending_withdrawals_list', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function withdrawalCancel(
  input: WithdrawalCancelInput,
): Promise<Result<WithdrawalCancelResult, AppError>> {
  try {
    const value = await invoke<WithdrawalCancelResult>('withdrawal_cancel', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-WITHDRAWAL-05 -- guardian marks/unmarks a posted withdrawal as
// reconciled. Reversible (REQ-4.5). Children are rejected by the
// command's resolve_guardian_actor gate.
export interface WithdrawalSetReconciledInput {
  sessionId: string;
  withdrawalId: number;
  /** True to mark as reconciled, false to clear an existing reconciliation. */
  reconciled: boolean;
}

export async function withdrawalSetReconciled(
  input: WithdrawalSetReconciledInput,
): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('withdrawal_set_reconciled', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function historyList(
  input: HistoryListInput,
): Promise<Result<HistoryEntry[], AppError>> {
  try {
    const value = await invoke<HistoryEntry[]>('history_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketsList(
  input: BucketsListInput,
): Promise<Result<BucketEntry[], AppError>> {
  try {
    const value = await invoke<BucketEntry[]>('buckets_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketAdd(input: BucketAddInput): Promise<Result<BucketAddOutput, AppError>> {
  try {
    const value = await invoke<BucketAddOutput>('bucket_add', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketUpdate(
  input: BucketUpdateInput,
): Promise<Result<BucketUpdateOutput, AppError>> {
  try {
    const value = await invoke<BucketUpdateOutput>('bucket_update', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// ---- US-BUCKET-05 -- deactivate a guardian-managed bucket ------------------

export interface BucketDeactivateInput {
  sessionId: string;
  bucketId: number;
  /**
   * Required when the source bucket has a non-zero balance; must reference
   * another active guardian-managed bucket belonging to the same child.
   * Ignored (can be null) when the source is empty.
   */
  targetBucketId: number | null;
}

export interface BucketDeactivateOutput {
  bucketId: number;
  flexibleRebalance: { bucketId: number; newAllocationBps: number }[];
  movedAmountMinorUnits: number;
  /** Set only when a transfer row was created (non-zero balance path). */
  transferId: number | null;
}

export async function bucketDeactivate(
  input: BucketDeactivateInput,
): Promise<Result<BucketDeactivateOutput, AppError>> {
  try {
    const value = await invoke<BucketDeactivateOutput>('bucket_deactivate', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-INTEREST-02 -- list recent interest accruals for one bucket
// =============================================================================

export interface BucketInterestAccrualsListInput {
  sessionId: string;
  bucketId: number;
  /** Optional cap on returned rows (default 12, clamped to 1..=100). */
  limit?: number | null;
}

export interface BucketInterestAccrualEntry {
  interestAccrualId: number;
  periodStartAtUtc: string;
  periodEndAtUtc: string;
  principalMinorUnits: number;
  interestMinorUnits: number;
  rateBpsSnapshot: number;
  /** True iff this accrual updated the bucket balance. False = display-only. */
  wasCredited: boolean;
}

export async function bucketInterestAccrualsList(
  input: BucketInterestAccrualsListInput,
): Promise<Result<BucketInterestAccrualEntry[], AppError>> {
  try {
    const value = await invoke<BucketInterestAccrualEntry[]>('bucket_interest_accruals_list', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-BACKUP-01 / US-BACKUP-02 -- encrypted backup export + restore
// =============================================================================

export interface BackupExportInput {
  sessionId: string;
  /** Absolute path to write the archive. Parent directory must exist. */
  outputPath: string;
  /** At least 10 characters per REQ-1.2. */
  backupPassword: string;
}

export type BackupExportResult =
  | { outcome: 'success'; filePath: string; sizeBytes: number }
  | { outcome: 'weak_password'; minLength: number };

export async function backupExport(
  input: BackupExportInput,
): Promise<Result<BackupExportResult, AppError>> {
  try {
    const value = await invoke<BackupExportResult>('backup_export', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface BackupRestoreInput {
  sessionId: string;
  /** Absolute path to the `.pldb-backup` file. */
  archivePath: string;
  /** Password that was used to create the backup. */
  backupPassword: string;
  /** Current guardian's login password. Required because restore is
   *  destructive (replaces all data) -- session alone is not enough. */
  loginPassword: string;
}

export type BackupRestoreResult =
  | { outcome: 'success'; needsRestart: boolean }
  | { outcome: 'wrong_login_password' }
  | { outcome: 'wrong_backup_password' }
  | {
      outcome: 'backup_newer_than_app';
      backupSchemaVersion: number;
      appSchemaVersion: number;
    }
  | { outcome: 'archive_malformed'; reason: string };

export async function backupRestore(
  input: BackupRestoreInput,
): Promise<Result<BackupRestoreResult, AppError>> {
  try {
    const value = await invoke<BackupRestoreResult>('backup_restore', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// Bytes-based variants for Android (SAF content:// URIs).
// The frontend reads/writes the file via @tauri-apps/plugin-fs;
// Rust handles crypto and DB operations only.

export interface BackupExportBytesInput {
  sessionId: string;
  backupPassword: string;
}

export type BackupExportBytesResult =
  | { outcome: 'success'; archiveBytes: number[] }
  | { outcome: 'weak_password'; minLength: number };

export async function backupExportBytes(
  input: BackupExportBytesInput,
): Promise<Result<BackupExportBytesResult, AppError>> {
  try {
    const value = await invoke<BackupExportBytesResult>('backup_export_bytes', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface TrialMigrationTableManifest {
  sourceTable: string;
  rowCount: number;
  sourceTableSha256: string;
  primaryKeyColumns: string[];
}

export interface TrialMigrationRow {
  sourceTable: string;
  sourceOrdinal: number;
  sourceKey: string;
  sourceRowCanonical: string;
  sourceRowSha256: string;
}

export interface TrialMigrationExportOutput {
  sourceDatabaseSha256: string;
  sourceSchemaVersion: number;
  sourceManifest: Record<string, unknown>;
  guardianLocalAccountId: number;
  familyName: string;
  familyCurrency: 'GBP' | 'USD';
  tables: TrialMigrationTableManifest[];
  rows: TrialMigrationRow[];
}

export async function trialMigrationExport(input: {
  sessionId: string;
}): Promise<Result<TrialMigrationExportOutput, AppError>> {
  try {
    const wire = await invoke<
      Omit<TrialMigrationExportOutput, 'sourceManifest'> & {
        sourceManifest: string;
      }
    >('trial_migration_export', { input });
    const sourceManifest: unknown = JSON.parse(wire.sourceManifest);
    if (!sourceManifest || typeof sourceManifest !== 'object' || Array.isArray(sourceManifest)) {
      throw new Error('The trial export returned an invalid manifest.');
    }
    return ok({ ...wire, sourceManifest: sourceManifest as Record<string, unknown> });
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface BackupRestoreBytesInput {
  sessionId: string;
  archiveBytes: number[];
  backupPassword: string;
  loginPassword: string;
}

export async function backupRestoreBytes(
  input: BackupRestoreBytesInput,
): Promise<Result<BackupRestoreResult, AppError>> {
  try {
    const value = await invoke<BackupRestoreResult>('backup_restore_bytes', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

/**
 * Tell the backend to exit. The UI calls this after a successful restore
 * so the user can relaunch against the restored DB. No-op on dev mock.
 */
export async function backupAppExit(): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('backup_app_exit');
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-SETUP-04 -- additional guardians + deactivation
// =============================================================================

export interface GuardianAddInput {
  sessionId: string;
  displayName: string;
  password: string;
  displayCurrencyCode: string;
  childRelationships: {
    childAccountId: number;
    guardianTypeCode: string;
  }[];
}

export interface GuardianAddResult {
  accountId: number;
  displayName: string;
}

export async function guardianAdd(
  input: GuardianAddInput,
): Promise<Result<GuardianAddResult, AppError>> {
  try {
    const value = await invoke<GuardianAddResult>('guardian_add', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface GuardianDeactivateInput {
  sessionId: string;
  guardianAccountId: number;
}

export type GuardianDeactivateResult =
  | {
      outcome: 'success';
      guardianAccountId: number;
      deletedAtUtc: string;
      purgeAfterUtc: string;
    }
  /** Non-creator tried to remove someone else. Self-removal is fine. */
  | { outcome: 'not_authorized' }
  /** Would leave zero active guardians; refused unconditionally. */
  | { outcome: 'last_remaining_guardian' }
  /** Creator cannot be removed via this command (separate future story). */
  | { outcome: 'cannot_remove_family_creator' };

export async function guardianDeactivate(
  input: GuardianDeactivateInput,
): Promise<Result<GuardianDeactivateResult, AppError>> {
  try {
    const value = await invoke<GuardianDeactivateResult>('guardian_deactivate', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface RecoverableGuardianEntry {
  guardianAccountId: number;
  displayName: string;
  deletedAtUtc: string;
  purgeAfterUtc: string;
}

export async function guardianRecoveryList(input: {
  sessionId: string;
}): Promise<Result<RecoverableGuardianEntry[], AppError>> {
  try {
    const value = await invoke<RecoverableGuardianEntry[]>('guardian_recovery_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface GuardianRestoreInput {
  sessionId: string;
  guardianAccountId: number;
  displayName: string;
  newPassword: string;
}

export interface GuardianRestoreResult {
  guardianAccountId: number;
  displayName: string;
}

export async function guardianRestore(
  input: GuardianRestoreInput,
): Promise<Result<GuardianRestoreResult, AppError>> {
  try {
    const value = await invoke<GuardianRestoreResult>('guardian_restore', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-AUTH-07 -- PIN sign-in
// =============================================================================

export interface PinSetInput {
  sessionId: string;
  /** Exactly six numeric digits. Backend rejects anything else. */
  pin: string;
}

export interface PinSetOutput {
  /** True if a PIN already existed on this device and was replaced. */
  replaced: boolean;
}

export async function pinSet(input: PinSetInput): Promise<Result<PinSetOutput, AppError>> {
  try {
    const value = await invoke<PinSetOutput>('pin_set', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface PinRemoveInput {
  sessionId: string;
}

export interface PinRemoveOutput {
  removed: boolean;
}

export async function pinRemove(input: PinRemoveInput): Promise<Result<PinRemoveOutput, AppError>> {
  try {
    const value = await invoke<PinRemoveOutput>('pin_remove', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface PinStatusInput {
  accountId: number;
}

export interface PinStatusOutput {
  hasPin: boolean;
}

/** Pre-auth -- drives the "Use PIN" affordance on the login screen. */
export async function pinStatus(input: PinStatusInput): Promise<Result<PinStatusOutput, AppError>> {
  try {
    const value = await invoke<PinStatusOutput>('pin_status', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface PinStatusForSessionInput {
  sessionId: string;
}

export interface PinStatusForSessionOutput {
  hasPin: boolean;
}

/** Post-auth -- drives the "Set PIN" vs "Remove PIN" toggle in Settings. */
export async function pinStatusForSession(
  input: PinStatusForSessionInput,
): Promise<Result<PinStatusForSessionOutput, AppError>> {
  try {
    const value = await invoke<PinStatusForSessionOutput>('pin_status_for_session', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface PinLoginInput {
  accountId: number;
  /** Exactly six numeric digits; anything else counts as an immediate miss. */
  pin: string;
}

/**
 * Same-shape-as-AuthLoginResult for success/locked; plus a `failed` arm
 * carrying attempts_remaining and a `not_configured` arm for stale UI.
 */
export type PinLoginResult =
  | {
      outcome: 'success';
      sessionId: string;
      accountId: number;
      expiresAtUtc: string;
    }
  | {
      outcome: 'failed';
      /** -1 is a sentinel meaning "unknown" (e.g. malformed PIN). */
      attemptsRemaining: number;
    }
  | {
      outcome: 'locked';
      expiresAtUtc: string;
    }
  | { outcome: 'not_configured' };

export async function pinLogin(input: PinLoginInput): Promise<Result<PinLoginResult, AppError>> {
  try {
    const value = await invoke<PinLoginResult>('pin_login', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-FX-03 -- Exchange rates management
// =============================================================================
//
// The `exchange_rates` table is a guardian-curated lookup of recent
// rates per currency pair. It's purely a convenience reference for
// the new-deposit form's pre-populate behaviour. Per US-FX-02, deposits
// stamp their own rate snapshot at write time, so the rates table
// never affects historical deposits.

export interface ExchangeRateEntry {
  exchangeRateId: number;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  /** Source → target rate as parts-per-million. */
  ratePpm: number;
  effectiveAtUtc: string;
  enteredByDisplayName: string;
  createdAtUtc: string;
}

export interface ExchangeRatesListInput {
  sessionId: string;
  /** Optional cap (default 50, clamped to 1..=200 at the db layer). */
  limit?: number | null;
}

export async function exchangeRatesList(
  input: ExchangeRatesListInput,
): Promise<Result<ExchangeRateEntry[], AppError>> {
  try {
    const value = await invoke<ExchangeRateEntry[]>('exchange_rates_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface ExchangeRateAddInput {
  sessionId: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  /** Source → target rate as parts-per-million. */
  ratePpm: number;
  /** ISO-8601 datetime in UTC when this rate is effective from. */
  effectiveAtUtc: string;
}

export interface ExchangeRateAddResult {
  exchangeRateId: number;
}

export async function exchangeRateAdd(
  input: ExchangeRateAddInput,
): Promise<Result<ExchangeRateAddResult, AppError>> {
  try {
    const value = await invoke<ExchangeRateAddResult>('exchange_rate_add', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface ExchangeRateLookupInput {
  sessionId: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
}

export interface ExchangeRateLookupResult {
  /** Null when no rate has been entered for the pair yet. */
  ratePpm: number | null;
}

export async function exchangeRateMostRecentForPair(
  input: ExchangeRateLookupInput,
): Promise<Result<ExchangeRateLookupResult, AppError>> {
  try {
    const value = await invoke<ExchangeRateLookupResult>('exchange_rate_most_recent_for_pair', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-CHORE-08 -- child browses master list + picks a chore
// =============================================================================

export interface MasterListChoresInput {
  sessionId: string;
}

export interface MasterListChoreEntry {
  choreId: number;
  choreName: string;
  choreDescription: string | null;
  choreTypeCode: string;
  choreTypeLabel: string;
  zoneName: string | null;
  rewardMinorUnits: number;
  recurrencePattern: string | null;
  requiresApprovalToClaim: boolean;
  /** True iff the calling child has a pending claim on this chore. */
  hasPendingClaimFromMe: boolean;
}

export async function masterListChoresList(
  input: MasterListChoresInput,
): Promise<Result<MasterListChoreEntry[], AppError>> {
  try {
    const value = await invoke<MasterListChoreEntry[]>('master_list_chores_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface ChorePickInput {
  sessionId: string;
  choreId: number;
}

export interface ChorePickResult {
  choreId: number;
  /** 'assigned' = chore flipped to caller; 'pending' = guardian must approve. */
  outcome: 'assigned' | 'pending';
  pendingChoreClaimId: number | null;
}

export async function chorePick(input: ChorePickInput): Promise<Result<ChorePickResult, AppError>> {
  try {
    const value = await invoke<ChorePickResult>('chore_pick', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-CHORE-09 -- guardian decides on pending chore claims
// =============================================================================

export interface PendingChoreClaimsListInput {
  sessionId: string;
}

export interface PendingChoreClaimEntry {
  pendingChoreClaimId: number;
  choreId: number;
  choreName: string;
  rewardMinorUnits: number;
  requestingChildAccountId: number;
  requestingChildDisplayName: string;
  requestedAtUtc: string;
}

export async function pendingChoreClaimsList(
  input: PendingChoreClaimsListInput,
): Promise<Result<PendingChoreClaimEntry[], AppError>> {
  try {
    const value = await invoke<PendingChoreClaimEntry[]>('pending_chore_claims_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface PendingChoreClaimDecideInput {
  sessionId: string;
  pendingChoreClaimId: number;
  approve: boolean;
  reason?: string;
}

export interface PendingChoreClaimDecideResult {
  pendingChoreClaimId: number;
  outcome: 'approved' | 'rejected';
  autoRejectedSiblingClaimCount: number;
}

export async function pendingChoreClaimDecide(
  input: PendingChoreClaimDecideInput,
): Promise<Result<PendingChoreClaimDecideResult, AppError>> {
  try {
    const value = await invoke<PendingChoreClaimDecideResult>('pending_chore_claim_decide', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-BUCKET-09 / US-BUCKET-10 / US-BUCKET-11 -- bucket transfers
// =============================================================================

export interface TransferSubmitInput {
  sessionId: string;
  sourceBucketId: number;
  targetBucketId: number;
  amountMinorUnits: number;
  note?: string;
}

export interface TransferSubmitResult {
  bucketTransferId: number;
  sourceBucketId: number;
  sourceBucketName: string;
  targetBucketId: number;
  targetBucketName: string;
  amountMinorUnits: number;
}

export async function transferSubmit(
  input: TransferSubmitInput,
): Promise<Result<TransferSubmitResult, AppError>> {
  try {
    const value = await invoke<TransferSubmitResult>('transfer_submit', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface PendingTransfersListInput {
  sessionId: string;
}

export interface PendingTransferEntry {
  bucketTransferId: number;
  childAccountId: number;
  childDisplayName: string;
  sourceBucketId: number;
  sourceBucketName: string;
  /** US-BUCKET-12: null when this is a zero-balance deactivation-only request. */
  targetBucketId: number | null;
  targetBucketName: string | null;
  amountMinorUnits: number;
  note: string | null;
  requestedAtUtc: string;
  /** US-BUCKET-12: when true, approval also deactivates the source bucket
   *  (delete-with-target flow). UI uses this to render a different label. */
  deactivateSourceOnApprove: boolean;
}

export async function pendingTransfersList(
  input: PendingTransfersListInput,
): Promise<Result<PendingTransferEntry[], AppError>> {
  try {
    const value = await invoke<PendingTransferEntry[]>('pending_transfers_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface TransferApproveInput {
  sessionId: string;
  bucketTransferId: number;
}

export interface TransferApproveResult {
  bucketTransferId: number;
  sourceBucketId: number;
  sourceBucketName: string;
  targetBucketId: number | null;
  targetBucketName: string | null;
  amountMinorUnits: number;
  newSourceBalanceMinorUnits: number;
  /** US-BUCKET-12: true when source bucket was deactivated as part of the
   *  approval. Drives "Approved deletion" vs "Approved transfer" flash. */
  sourceDeactivated: boolean;
}

export async function transferApprove(
  input: TransferApproveInput,
): Promise<Result<TransferApproveResult, AppError>> {
  try {
    const value = await invoke<TransferApproveResult>('transfer_approve', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface TransferRejectInput {
  sessionId: string;
  bucketTransferId: number;
  reason: string;
}

export interface TransferRejectResult {
  bucketTransferId: number;
}

export async function transferReject(
  input: TransferRejectInput,
): Promise<Result<TransferRejectResult, AppError>> {
  try {
    const value = await invoke<TransferRejectResult>('transfer_reject', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface MyPendingTransfersListInput {
  sessionId: string;
}

export interface MyPendingTransferEntry {
  bucketTransferId: number;
  sourceBucketId: number;
  sourceBucketName: string;
  targetBucketId: number | null;
  targetBucketName: string | null;
  amountMinorUnits: number;
  note: string | null;
  requestedAtUtc: string;
  deactivateSourceOnApprove: boolean;
}

export async function myPendingTransfersList(
  input: MyPendingTransfersListInput,
): Promise<Result<MyPendingTransferEntry[], AppError>> {
  try {
    const value = await invoke<MyPendingTransferEntry[]>('my_pending_transfers_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface TransferCancelInput {
  sessionId: string;
  bucketTransferId: number;
}

export interface TransferCancelResult {
  bucketTransferId: number;
}

export async function transferCancel(
  input: TransferCancelInput,
): Promise<Result<TransferCancelResult, AppError>> {
  try {
    const value = await invoke<TransferCancelResult>('transfer_cancel', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-BUCKET-12 -- child requests bucket deletion (with optional target)
// =============================================================================

export interface BucketDeleteRequestInput {
  sessionId: string;
  sourceBucketId: number;
  /** Required when source has a positive balance; null/omitted for
   *  zero-balance deletion (deactivate-only request). */
  targetBucketId?: number | null;
  note?: string;
}

export interface BucketDeleteRequestResult {
  bucketTransferId: number;
  sourceBucketId: number;
  sourceBucketName: string;
  targetBucketId: number | null;
  targetBucketName: string | null;
  amountMinorUnits: number;
}

export async function bucketDeleteRequest(
  input: BucketDeleteRequestInput,
): Promise<Result<BucketDeleteRequestResult, AppError>> {
  try {
    const value = await invoke<BucketDeleteRequestResult>('bucket_delete_request', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-HISTORY-03 -- consolidated history across linked children
// =============================================================================

export interface ConsolidatedHistoryListInput {
  sessionId: string;
  limit?: number;
  atFromUtc?: string;
  atToUtc?: string;
  kindFilter?: string;
  bucketIdFilter?: number;
}

export interface ConsolidatedHistoryEntry {
  childAccountId: number;
  childDisplayName: string;
  entry: HistoryEntry;
}

export async function consolidatedHistoryList(
  input: ConsolidatedHistoryListInput,
): Promise<Result<ConsolidatedHistoryEntry[], AppError>> {
  try {
    const value = await invoke<ConsolidatedHistoryEntry[]>('consolidated_history_list', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-HISTORY-05 -- guardian audit log view
// =============================================================================

export interface AuditLogListInput {
  sessionId: string;
  limit?: number;
  eventCode?: string;
  actorAccountIdFilter?: number;
  atFromUtc?: string;
  atToUtc?: string;
}

export interface AuditLogEntry {
  auditEventId: number;
  eventAtUtc: string;
  eventCode: string;
  eventLabel: string;
  eventCategory: string;
  actorAccountId: number | null;
  actorDisplayName: string | null;
  subjectAccountId: number | null;
  subjectDisplayName: string | null;
  subjectEntityTypeCode: string | null;
  subjectEntityId: number | null;
  eventDetailsJson: string | null;
}

export async function auditLogList(
  input: AuditLogListInput,
): Promise<Result<AuditLogEntry[], AppError>> {
  try {
    const value = await invoke<AuditLogEntry[]>('audit_log_list', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-DEV-TESTDATA -- surface the build flavour to the UI
// =============================================================================

/** Returns true iff this binary was compiled with the `test-data`
 *  Cargo feature on. The UI uses this to render the orange banner +
 *  adjust the window title. */
export async function isTestDataBuild(): Promise<Result<boolean, AppError>> {
  try {
    const value = await invoke<boolean>('is_test_data_build');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-AUTH-08 -- Biometric sign-in (Windows Hello / Touch ID / Face ID /
// Android fingerprint)
//
// The client wrapper mirrors the backend command surface in
// src-tauri/src/commands/biometric.rs. The feature is deliberately
// fail-closed unless an OS biometric prompt and biometric-bound secure
// credential storage are both configured. Children never receive this path.
// =============================================================================

export type BiometricSupport = 'available' | 'unavailable';

export interface BiometricSupportOutput {
  support: BiometricSupport;
  /** Empty when `available`. Non-empty `unavailable` reason is
   *  developer-facing (visible in devtools) -- UI typically renders
   *  nothing rather than this string per AC #8 (silently hide). */
  reason: string;
  /** Platform-facing name such as Face ID, Touch ID, or fingerprint. */
  biometryLabel: string | null;
}

/** Pre-auth capability probe. Unsupported platforms remain hidden from the
 * login flow instead of receiving a non-functional enrolment control. */
export async function biometricSupport(): Promise<Result<BiometricSupportOutput, AppError>> {
  try {
    const value = await invoke<BiometricSupportOutput>('biometric_support');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface BiometricStatusForAccountInput {
  accountId: number;
}

export interface BiometricStatusForAccountOutput {
  enabled: boolean;
  biometryLabel: string | null;
}

/** Pre-auth -- drives the "Use biometrics" affordance on the login
 *  screen for a specific account. */
export async function biometricStatusForAccount(
  input: BiometricStatusForAccountInput,
): Promise<Result<BiometricStatusForAccountOutput, AppError>> {
  try {
    const value = await invoke<BiometricStatusForAccountOutput>('biometric_status_for_account', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface BiometricEnableForAccountInput {
  sessionId: string;
  /** Guardian selected as this device's single main adult. */
  accountId: number;
  /** PIN to wrap into the biometric envelope. Biometric is layered
   *  on top of PIN -- biometric success unlocks the wrapped PIN
   *  hash and feeds it to the existing pin_login flow. */
  pin: string;
}

export interface BiometricEnableForAccountOutput {
  enabled: boolean;
}

/** Post-auth -- enable biometric for the signed-in guardian account on this
 * device. The native layer rejects the request when secure biometric
 * credential storage is unavailable. */
export async function biometricEnableForAccount(
  input: BiometricEnableForAccountInput,
): Promise<Result<BiometricEnableForAccountOutput, AppError>> {
  try {
    const value = await invoke<BiometricEnableForAccountOutput>('biometric_enable_for_account', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface BiometricDisableForAccountInput {
  sessionId: string;
  accountId: number;
}

export interface BiometricDisableForAccountOutput {
  /** True if a keyring entry was removed; false if it was already
   *  absent. Either way the post-condition is "no biometric for
   *  this account on this device". Idempotent and safe to call. */
  wasEnabled: boolean;
}

/** Post-auth -- disable biometric for the signed-in account on this
 *  device. Idempotent. */
export async function biometricDisableForAccount(
  input: BiometricDisableForAccountInput,
): Promise<Result<BiometricDisableForAccountOutput, AppError>> {
  try {
    const value = await invoke<BiometricDisableForAccountOutput>('biometric_disable_for_account', {
      input,
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface BiometricLoginInput {
  accountId: number;
}

export type BiometricLoginResult =
  | {
      outcome: 'success';
      sessionId: string;
      accountId: number;
      expiresAtUtc: string;
    }
  | { outcome: 'cancelled' }
  | { outcome: 'failed' }
  | { outcome: 'notSupported' }
  | { outcome: 'notEnabled' };

/** Pre-auth -- sign in via biometrics for a guardian previously enrolled on
 * this device. Unsupported or unconfigured native platforms return
 * `notSupported` rather than falling back to an insecure credential path. */
export async function biometricLogin(
  input: BiometricLoginInput,
): Promise<Result<BiometricLoginResult, AppError>> {
  try {
    const value = await invoke<BiometricLoginResult>('biometric_login', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-BUCKET-15 / US-BUCKET-17 -- Guardian sets a bucket balance directly
// =============================================================================

export interface BucketBalanceAdjustInput {
  sessionId: string;
  bucketId: number;
  newBalanceMinorUnits: number;
  reason: string;
  /** 'balance_adjustment' for US-BUCKET-15 corrections;
   *  'initial_balance' for US-BUCKET-17 setup-time seeding. */
  sourceCode: 'balance_adjustment' | 'initial_balance';
}

export interface BucketBalanceAdjustOutput {
  adjustmentId: number;
  bucketId: number;
  oldBalanceMinorUnits: number;
  newBalanceMinorUnits: number;
  deltaMinorUnits: number;
}

export async function bucketBalanceAdjust(
  input: BucketBalanceAdjustInput,
): Promise<Result<BucketBalanceAdjustOutput, AppError>> {
  try {
    const value = await invoke<BucketBalanceAdjustOutput>('bucket_balance_adjust', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-BUCKET-16 -- Guardian direct inter-bucket transfer
// =============================================================================

export interface GuardianBucketTransferInput {
  sessionId: string;
  sourceBucketId: number;
  targetBucketId: number;
  amountMinorUnits: number;
  note?: string;
}

export interface GuardianBucketTransferOutput {
  transferId: number;
  sourceBucketId: number;
  sourceBucketName: string;
  targetBucketId: number;
  targetBucketName: string;
  amountMinorUnits: number;
  sourceBalanceAfter: number;
  targetBalanceAfter: number;
}

export async function guardianBucketTransfer(
  input: GuardianBucketTransferInput,
): Promise<Result<GuardianBucketTransferOutput, AppError>> {
  try {
    const value = await invoke<GuardianBucketTransferOutput>('guardian_bucket_transfer', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-NET-01..06 -- LAN Sync network commands
// =============================================================================

export interface NetworkEnableMasterInput {
  sessionId: string;
  deviceName: string;
  port?: number;
}

export interface NetworkEnableMasterOutput {
  token: string;
  pairingQrSvg: string;
  pairingUri: string;
  lanIp: string;
  port: number;
}

export interface NetworkPairingQrOutput {
  pairingQrSvg: string;
  pairingUri: string;
  tokenHint: string;
  lanIp: string;
  port: number;
}

export interface NetworkPairWithMasterInput {
  sessionId: string;
  pairingUri?: string;
  masterHost?: string;
  masterPort?: number;
  token?: string;
  deviceName?: string;
}

export interface NetworkPairWithMasterOutput {
  masterDeviceName: string;
  masterHost: string;
  masterPort: number;
  connectionOk: boolean;
}

export interface NetworkStatusOutput {
  role: string;
  statusJson: string;
  unresolvedConflicts: number;
  pendingQueueCount: number;
}

export interface NetworkConnectionCheckOutput {
  connected: boolean;
  latencyMs: number | null;
  masterDeviceName: string | null;
  masterSeq: number | null;
}

export interface NetworkSyncNowOutput {
  connected: boolean;
  applied: number;
  conflicts: number;
  failed: number;
  snapshotRefreshed: boolean;
  masterSeq: number;
  clientSeq: number;
}

export interface NetworkConflictEntry {
  conflictId: number;
  clientDeviceId: string;
  operationCode: string;
  entityType: string;
  entityId: number | null;
  expectedBalanceBefore: number | null;
  actualBalanceAtMerge: number | null;
  conflictDescription: string;
  createdAtUtc: string;
}

export interface NetworkResolveConflictInput {
  sessionId: string;
  conflictId: number;
  resolutionNote: string;
}

export async function networkGetStatus(
  sessionId: string,
): Promise<Result<NetworkStatusOutput, AppError>> {
  try {
    const value = await invoke<NetworkStatusOutput>('network_get_status', { sessionId });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkEnableMaster(
  input: NetworkEnableMasterInput,
): Promise<Result<NetworkEnableMasterOutput, AppError>> {
  try {
    const value = await invoke<NetworkEnableMasterOutput>('network_enable_master', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkGetPairingQr(
  sessionId: string,
): Promise<Result<NetworkPairingQrOutput, AppError>> {
  try {
    const value = await invoke<NetworkPairingQrOutput>('network_get_pairing_qr', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkPairWithMaster(
  input: NetworkPairWithMasterInput,
): Promise<Result<NetworkPairWithMasterOutput, AppError>> {
  try {
    const value = await invoke<NetworkPairWithMasterOutput>('network_pair_with_master', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkDisable(sessionId: string): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('network_disable', { input: { sessionId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkSyncNow(
  sessionId: string,
  forceSnapshot = false,
): Promise<Result<NetworkSyncNowOutput, AppError>> {
  try {
    const value = await invoke<NetworkSyncNowOutput>('network_sync_now', {
      input: { sessionId, forceSnapshot },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkConnectionCheck(): Promise<
  Result<NetworkConnectionCheckOutput, AppError>
> {
  try {
    const value = await invoke<NetworkConnectionCheckOutput>('network_connection_check');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkListConflicts(
  sessionId: string,
): Promise<Result<NetworkConflictEntry[], AppError>> {
  try {
    const value = await invoke<NetworkConflictEntry[]>('network_list_conflicts', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function networkResolveConflict(
  input: NetworkResolveConflictInput,
): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('network_resolve_conflict', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-GIFT-01..06 -- Gift deposits
// =============================================================================

export interface GiftCreateInput {
  sessionId: string;
  childAccountId: number;
  amountMinorUnits: number;
  note?: string | null;
}

export interface GiftCreateOutput {
  depositId: number;
  autoAllocated: boolean;
}

export interface PendingGiftEntry {
  depositId: number;
  childAccountId: number;
  childDisplayName: string;
  amountMinorUnits: number;
  note: string | null;
  createdAtUtc: string;
}

export interface BucketAllocation {
  bucketId: number;
  amountMinorUnits: number;
}

export interface GiftAllocateInput {
  sessionId: string;
  depositId: number;
  allocations: BucketAllocation[];
}

export async function giftCreate(
  input: GiftCreateInput,
): Promise<Result<GiftCreateOutput, AppError>> {
  try {
    const value = await invoke<GiftCreateOutput>('gift_create', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function giftPendingListForGuardian(
  sessionId: string,
): Promise<Result<PendingGiftEntry[], AppError>> {
  try {
    const value = await invoke<PendingGiftEntry[]>('gift_pending_list_for_guardian', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function giftMyPendingList(
  sessionId: string,
): Promise<Result<PendingGiftEntry[], AppError>> {
  try {
    const value = await invoke<PendingGiftEntry[]>('gift_my_pending_list', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface GiftDebugRow {
  depositId: number;
  amountMinorUnits: number;
  statusCode: string;
  sourceCode: string;
  childAccountId: number;
  actorAccountId: number;
  requestedAtUtc: string;
}

/** Diagnostic: all deposits for the logged-in child, any status. */
export async function giftDebugDeposits(
  sessionId: string,
): Promise<Result<GiftDebugRow[], AppError>> {
  try {
    const value = await invoke<GiftDebugRow[]>('gift_debug_deposits', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function giftAllocate(input: GiftAllocateInput): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('gift_allocate', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function giftCancel(
  sessionId: string,
  depositId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('gift_cancel', { input: { sessionId, depositId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-NET-11 -- Join server at first launch + in-app QR scanner
// =============================================================================

export interface PairAtSetupInput {
  pairingUri?: string | null;
  masterHost?: string | null;
  masterPort?: number | null;
  token?: string | null;
  deviceName?: string | null;
}

export async function networkPairAtSetup(
  input: PairAtSetupInput,
): Promise<Result<NetworkPairWithMasterOutput, AppError>> {
  try {
    const value = await invoke<NetworkPairWithMasterOutput>('network_pair_at_setup', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

/**
 * Open the device's camera to scan a QR code.
 * Only available on Android/iOS -- throws on desktop with a clear message.
 * Callers should catch and show a fallback.
 */
export async function scanQrCode(): Promise<string> {
  // Use direct Tauri invoke so this file never statically imports the
  // barcode-scanner plugin (mobile-only; absent from desktop builds).
  // On mobile the plugin registers the command; on desktop the invoke
  // throws and the caller shows a "not available" message.
  //
  // Tauri/plugin errors arrive as plain objects {message:string} rather
  // than Error instances. Re-throw as a real Error so callers get a
  // consistent .message property instead of "[object Object]".
  try {
    const result = await invoke<{ content: string; format: string }>(
      'plugin:barcode-scanner|scan',
      { formats: ['QRCode'], windowed: false },
    );
    return result.content;
  } catch (e: unknown) {
    const raw = e as unknown;
    const msg: string =
      raw instanceof Error
        ? raw.message
        : typeof raw === 'string'
          ? raw
          : typeof (raw as Record<string, unknown>)?.message === 'string'
            ? (raw as { message: string }).message
            : (JSON.stringify(raw) ?? 'scan failed');
    throw new Error(msg);
  }
}

// =============================================================================
// US-DEVICE-01 -- Device registry and assignment
// =============================================================================

export interface DeviceEntry {
  deviceId: string;
  deviceName: string;
  assignedAccountId: number | null;
  assignedAccountName: string | null;
  isRevoked: boolean;
  registeredAtUtc: string;
  updatedAtUtc: string;
}

export interface DeviceRegisterInput {
  deviceName: string;
}

export interface DeviceRegisterOutput {
  deviceId: string;
  assignment: DeviceEntry | null;
}

/** Called on every app startup. Registers this device and returns its
 *  current assignment (if any). No auth required. */
export async function deviceRegister(
  input: DeviceRegisterInput,
): Promise<Result<DeviceRegisterOutput, AppError>> {
  try {
    const value = await invoke<DeviceRegisterOutput>('device_register', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function deviceList(sessionId: string): Promise<Result<DeviceEntry[], AppError>> {
  try {
    const value = await invoke<DeviceEntry[]>('device_list', { input: { sessionId } });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface DeviceAssignInput {
  sessionId: string;
  deviceId: string;
  childAccountId: number;
}

export async function deviceAssign(
  input: DeviceAssignInput,
): Promise<Result<DeviceEntry, AppError>> {
  try {
    const value = await invoke<DeviceEntry>('device_assign', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function deviceUnassign(
  sessionId: string,
  deviceId: string,
): Promise<Result<DeviceEntry, AppError>> {
  try {
    const value = await invoke<DeviceEntry>('device_unassign', {
      input: { sessionId, deviceId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function deviceRevoke(
  sessionId: string,
  deviceId: string,
): Promise<Result<DeviceEntry, AppError>> {
  try {
    const value = await invoke<DeviceEntry>('device_revoke', {
      input: { sessionId, deviceId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// Guardian credential challenge (parental settings gate on child devices)

export type GuardianCredential =
  | { kind: 'password'; password: string }
  | { kind: 'pin'; pin: string };

export interface GuardianChallengeInput {
  accountId: number;
  credential: GuardianCredential;
}

export type GuardianChallengeResult =
  | { outcome: 'success'; settingsToken: string }
  | { outcome: 'failed'; attemptsRemaining: number }
  | { outcome: 'locked'; expiresAtUtc: string }
  | { outcome: 'notGuardian' };

export async function guardianChallenge(
  input: GuardianChallengeInput,
): Promise<Result<GuardianChallengeResult, AppError>> {
  try {
    const value = await invoke<GuardianChallengeResult>('guardian_challenge', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

/** Return the device_id string for this device. */
export async function deviceGetId(): Promise<Result<string, AppError>> {
  try {
    const value = await invoke<string>('device_get_id');
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-ROUTINE-01..07 -- Recurring tasks
// =============================================================================

export interface SubTaskEntry {
  subTaskId: number;
  sortOrder: number;
  subTaskName: string;
}

export interface RoutineEntry {
  recurringTaskId: number;
  childAccountId: number;
  taskName: string;
  taskNotes: string | null;
  rewardMinorUnits: number;
  missBehaviour: 'expire_quietly' | 'record_miss' | 'penalty' | 'record_and_penalty';
  penaltyBucketId: number | null;
  penaltyMinorUnits: number;
  isPaused: boolean;
  pauseUntilDate: string | null;
  repeatIntervalWeeks: number;
  scheduleDays: number[];
  visibleTime: string;
  deadlineTime: string;
  subTasks: SubTaskEntry[];
}

export interface RoutineInstanceEntry {
  instanceId: number;
  recurringTaskId: number;
  /** Present for guardian queues; child callers receive their own identity. */
  childAccountId?: number;
  childDisplayName?: string;
  scheduledDate: string;
  taskName: string;
  rewardMinorUnits: number;
  deadlineTime: string;
  status: 'pending_child' | 'pending_guardian' | 'approved' | 'rejected' | 'missed';
  submittedAtUtc: string | null;
  decidedAtUtc: string | null;
  rejectionReason: string | null;
  subTasks: SubTaskEntry[];
  tickedSubTaskIds: number[];
}

export interface RoutineStreaks {
  recurringTaskId: number;
  currentStreak: number;
  bestStreak: number;
}

export interface RoutineBadgeCounts {
  pendingApprovalCount: number;
  pendingChildTodayCount: number;
}

export interface FamilyChallengeChildProgress {
  childAccountId: number;
  displayName: string;
  completedDays: number;
}
export interface FamilyChallengeProgress {
  familyChallengeId: number;
  challengeName: string;
  targetDaysPerChild: number;
  startsOn: string;
  endsOn: string;
  children: FamilyChallengeChildProgress[];
}

export async function familyChallengeCurrent(
  sessionId: string,
): Promise<Result<FamilyChallengeProgress | null, AppError>> {
  try {
    return ok(
      await invoke<FamilyChallengeProgress | null>('family_challenge_current', {
        input: { sessionId },
      }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}
export async function familyChallengeStart(input: {
  sessionId: string;
  challengeName: string;
  targetDaysPerChild: number;
}): Promise<Result<FamilyChallengeProgress, AppError>> {
  try {
    return ok(await invoke<FamilyChallengeProgress>('family_challenge_start', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function familyChallengeEnd(input: {
  sessionId: string;
  familyChallengeId: number;
}): Promise<Result<void, AppError>> {
  try {
    await invoke<void>('family_challenge_end', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface CreateRoutineInput {
  sessionId: string;
  childAccountId: number;
  taskName: string;
  taskNotes?: string;
  rewardMinorUnits: number;
  missBehaviour: string;
  penaltyBucketId?: number | null;
  penaltyMinorUnits: number;
  scheduleDays: number[];
  repeatIntervalWeeks: number;
  visibleTime: string;
  deadlineTime: string;
  subTasks: string[];
}

export async function routineCreate(
  input: CreateRoutineInput,
): Promise<Result<{ recurringTaskId: number }, AppError>> {
  try {
    const value = await invoke<{ recurringTaskId: number }>('routine_create', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routinesListForGuardian(
  sessionId: string,
): Promise<Result<RoutineEntry[], AppError>> {
  try {
    const value = await invoke<RoutineEntry[]>('routines_list_for_guardian', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routinesListForChild(
  sessionId: string,
): Promise<Result<RoutineEntry[], AppError>> {
  try {
    const value = await invoke<RoutineEntry[]>('routines_list_for_child', { input: { sessionId } });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routinesGenerateToday(
  sessionId: string,
): Promise<Result<{ created: number; missedProcessed: number }, AppError>> {
  try {
    const value = await invoke<{ created: number; missedProcessed: number }>(
      'routines_generate_today',
      { input: { sessionId } },
    );
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineInstancesToday(
  sessionId: string,
): Promise<Result<RoutineInstanceEntry[], AppError>> {
  try {
    const value = await invoke<RoutineInstanceEntry[]>('routine_instances_today', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineTickSubTask(
  sessionId: string,
  instanceId: number,
  subTaskId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_tick_sub_task', { input: { sessionId, instanceId, subTaskId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineSubmit(
  sessionId: string,
  instanceId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_submit', { input: { sessionId, instanceId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineResubmit(
  sessionId: string,
  instanceId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_resubmit', { input: { sessionId, instanceId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routinePendingApprovals(
  sessionId: string,
): Promise<Result<RoutineInstanceEntry[], AppError>> {
  try {
    const value = await invoke<RoutineInstanceEntry[]>('routine_pending_approvals', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineApprove(
  sessionId: string,
  instanceId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_approve', { input: { sessionId, instanceId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineReject(
  sessionId: string,
  instanceId: number,
  reason: string,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_reject', { input: { sessionId, instanceId, reason } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface UpdateRoutineInput {
  sessionId: string;
  recurringTaskId: number;
  taskName?: string;
  taskNotes?: string | null;
  rewardMinorUnits?: number;
  missBehaviour?: string;
  penaltyBucketId?: number | null;
  penaltyMinorUnits?: number;
  scheduleDays?: number[];
  repeatIntervalWeeks?: number;
  visibleTime?: string;
  deadlineTime?: string;
  subTasks?: string[];
}

export async function routineUpdate(input: UpdateRoutineInput): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_update', { input });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routinePause(
  sessionId: string,
  recurringTaskId: number,
  pauseUntilDate: string | null,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_pause', { input: { sessionId, recurringTaskId, pauseUntilDate } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineResume(
  sessionId: string,
  recurringTaskId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_resume', { input: { sessionId, recurringTaskId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineDelete(
  sessionId: string,
  recurringTaskId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('routine_delete', { input: { sessionId, recurringTaskId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineHistory(
  sessionId: string,
  recurringTaskId: number,
  limit?: number,
  beforeDate?: string,
): Promise<Result<RoutineInstanceEntry[], AppError>> {
  try {
    const value = await invoke<RoutineInstanceEntry[]>('routine_history', {
      input: { sessionId, recurringTaskId, limit, beforeDate },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineStreaks(
  sessionId: string,
  recurringTaskId: number,
): Promise<Result<RoutineStreaks, AppError>> {
  try {
    const value = await invoke<RoutineStreaks>('routine_streaks', {
      input: { sessionId, recurringTaskId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function routineBadgeCounts(
  sessionId: string,
): Promise<Result<RoutineBadgeCounts, AppError>> {
  try {
    const value = await invoke<RoutineBadgeCounts>('routine_badge_counts', {
      input: { sessionId },
    });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-ALLOWANCE-01 -- Recurring allowance
// =============================================================================

export interface AllowanceSchedule {
  scheduleId: number;
  childAccountId: number;
  childDisplayName: string;
  createdByAccountId: number;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  amountMinorUnits: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  startDate: string;
  isActive: boolean;
  pausedUntilDate: string | null;
  lastGeneratedDate: string | null;
  label: string;
}

export interface CreateAllowanceInput {
  sessionId: string;
  childAccountId: number;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  amountMinorUnits: number;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  startDate: string;
  label?: string;
}

export async function allowanceCreate(
  input: CreateAllowanceInput,
): Promise<Result<number, AppError>> {
  try {
    return ok(await invoke<number>('allowance_create', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function allowanceList(
  sessionId: string,
): Promise<Result<AllowanceSchedule[], AppError>> {
  try {
    return ok(await invoke<AllowanceSchedule[]>('allowance_list', { input: { sessionId } }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function allowancePause(
  sessionId: string,
  scheduleId: number,
  pauseUntilDate: string | null,
): Promise<Result<void, AppError>> {
  try {
    await invoke('allowance_pause', { input: { sessionId, scheduleId, pauseUntilDate } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function allowanceCancel(
  sessionId: string,
  scheduleId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('allowance_cancel', { input: { sessionId, scheduleId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function allowanceGenerateDue(
  sessionId: string,
): Promise<Result<{ created: number }, AppError>> {
  try {
    return ok(
      await invoke<{ created: number }>('allowance_generate_due', { input: { sessionId } }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// Child-facing approval notices
// =============================================================================

export interface ChildApprovalNotice {
  childApprovalNoticeId: number;
  noticeType: 'deposit' | 'withdrawal';
  sourceEntityId: number;
  amountMinorUnits: number;
  currencyCode: string;
  approvedAtUtc: string;
}

export async function childApprovalNoticesList(
  sessionId: string,
): Promise<Result<ChildApprovalNotice[], AppError>> {
  try {
    return ok(
      await invoke<ChildApprovalNotice[]>('child_approval_notices_list', {
        input: { sessionId },
      }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-GOAL-01/02 -- Savings goals
// =============================================================================

export interface SavingsGoal {
  goalId: number;
  childAccountId: number;
  bucketId: number | null;
  bucketName: string | null;
  currentMinorUnits: number;
  goalName: string;
  goalNotes: string | null;
  targetMinorUnits: number;
  status: 'active' | 'completed' | 'cancelled';
  progressPct: number;
  matchBps: number | null;
  matchFixedMinorUnits: number | null;
  goalEmoji: string | null;
  goalType: 'saving' | 'giving';
  createdAtUtc: string;
  completedAtUtc: string | null;
}

export interface CreateGoalInput {
  sessionId: string;
  bucketId?: number | null;
  goalName: string;
  goalNotes?: string;
  targetMinorUnits: number;
  matchBps?: number | null;
  matchFixedMinorUnits?: number | null;
  goalEmoji?: string | null;
  goalType?: 'saving' | 'giving';
}

export async function goalCreate(input: CreateGoalInput): Promise<Result<number, AppError>> {
  try {
    return ok(await invoke<number>('goal_create', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function goalsList(sessionId: string): Promise<Result<SavingsGoal[], AppError>> {
  try {
    return ok(await invoke<SavingsGoal[]>('goals_list', { input: { sessionId } }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function goalComplete(
  sessionId: string,
  goalId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('goal_complete', { input: { sessionId, goalId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function goalCancel(
  sessionId: string,
  goalId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('goal_cancel', { input: { sessionId, goalId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-REPORT-02 -- CSV export
// =============================================================================

export interface ExportCsvInput {
  sessionId: string;
  childAccountId?: number;
  fromDate?: string;
  toDate?: string;
  entryType?: 'deposit' | 'withdrawal' | 'all';
  outputPath: string;
}

export async function reportExportCsv(
  input: ExportCsvInput,
): Promise<Result<{ rowsWritten: number; outputPath: string }, AppError>> {
  try {
    return ok(
      await invoke<{ rowsWritten: number; outputPath: string }>('report_export_csv', { input }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-REPORT-01 -- Monthly family statement
// =============================================================================

export interface MonthlyStatementBucket {
  bucketId: number;
  bucketName: string;
  openingMinorUnits: number;
  closingMinorUnits: number;
  totalDeposited: number;
  totalWithdrawn: number;
}

export interface MonthlyStatement {
  childAccountId: number;
  childDisplayName: string;
  currency: string;
  periodLabel: string; // e.g. "April 2025"
  openingBalanceMinorUnits: number;
  closingBalanceMinorUnits: number;
  totalDeposited: number;
  totalWithdrawn: number;
  totalTransferred: number;
  depositCount: number;
  withdrawalCount: number;
  buckets: MonthlyStatementBucket[];
}

export interface MonthlyStatementInput {
  sessionId: string;
  childAccountId: number;
  year: number;
  month: number; // 1-12
}

export async function reportMonthlyStatement(
  input: MonthlyStatementInput,
): Promise<Result<MonthlyStatement, AppError>> {
  try {
    const value = await invoke<MonthlyStatement>('report_monthly_statement', { input });
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-CHORE-PRO-01 -- Recurring chore schedules
// =============================================================================

export interface ChoreSchedule {
  scheduleId: number;
  createdByAccountId: number;
  childAccountId: number | null;
  childDisplayName: string | null;
  choreName: string;
  choreDescription: string | null;
  zoneName: string | null;
  rewardMinorUnits: number;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  startDate: string;
  isActive: boolean;
  pausedUntilDate: string | null;
  lastGeneratedDate: string | null;
  currentStreak: number;
  bestStreak: number;
}

export interface CreateChoreScheduleInput {
  sessionId: string;
  childAccountId?: number | null;
  choreName: string;
  choreDescription?: string;
  zoneName?: string;
  rewardMinorUnits: number;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  startDate: string;
}

export async function choreScheduleCreate(
  input: CreateChoreScheduleInput,
): Promise<Result<number, AppError>> {
  try {
    return ok(await invoke<number>('chore_schedule_create', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreSchedulesList(
  sessionId: string,
): Promise<Result<ChoreSchedule[], AppError>> {
  try {
    return ok(await invoke<ChoreSchedule[]>('chore_schedules_list', { input: { sessionId } }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreSchedulePause(
  sessionId: string,
  scheduleId: number,
  pauseUntilDate: string | null,
): Promise<Result<void, AppError>> {
  try {
    await invoke('chore_schedule_pause', { input: { sessionId, scheduleId, pauseUntilDate } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreScheduleCancel(
  sessionId: string,
  scheduleId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('chore_schedule_cancel', { input: { sessionId, scheduleId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function choreSchedulesGenerate(
  sessionId: string,
): Promise<Result<{ created: number }, AppError>> {
  try {
    return ok(
      await invoke<{ created: number }>('chore_schedules_generate', { input: { sessionId } }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

export interface MatchContributionInput {
  sessionId: string;
  goalId: number;
  amountMinorUnits: number;
  note?: string;
}

export async function goalAddMatch(
  input: MatchContributionInput,
): Promise<Result<{ contributionId: number; depositId: number | null }, AppError>> {
  try {
    const value = await invoke<{ contributionId: number; depositId: number | null }>(
      'goal_add_match',
      { input },
    );
    return ok(value);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-CUSTOM-01 -- Bucket templates
// =============================================================================

export interface TemplateBucketSpec {
  bucketName: string;
  allocationBps: number;
  percentIsFixed: boolean;
  requiresApprovalToWithdraw: boolean;
  isGuardianManaged: boolean;
  displayColor: string | null;
}

export interface BucketTemplate {
  templateId: number;
  createdByAccountId: number;
  templateName: string;
  configJson: string;
  createdAtUtc: string;
}

export interface CreateTemplateInput {
  sessionId: string;
  templateName: string;
  buckets: TemplateBucketSpec[];
}

export async function templateCreate(
  input: CreateTemplateInput,
): Promise<Result<number, AppError>> {
  try {
    return ok(await invoke<number>('template_create', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function templatesList(
  sessionId: string,
): Promise<Result<BucketTemplate[], AppError>> {
  try {
    return ok(await invoke<BucketTemplate[]>('templates_list', { input: { sessionId } }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function templateDelete(
  sessionId: string,
  templateId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('template_delete', { input: { sessionId, templateId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function templateApply(
  sessionId: string,
  templateId: number,
  childAccountId: number,
): Promise<Result<number, AppError>> {
  try {
    return ok(
      await invoke<number>('template_apply', { input: { sessionId, templateId, childAccountId } }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-REPORT-03 -- Chore completion trends
// =============================================================================

export interface WeeklyChoreStats {
  weekStartDate: string;
  childName: string;
  choreName: string;
  generated: number;
  completed: number;
  missed: number;
  rejected: number;
  completionPct: number;
}

export interface ChoreTrendsInput {
  sessionId: string;
  childAccountId?: number;
  fromWeek?: string;
  toWeek?: string;
}

// =============================================================================
// US-CUSTOM-01 -- Bucket configuration templates
// =============================================================================

export interface BucketTemplateItem {
  itemId: number;
  bucketName: string;
  displayColor: string | null;
  allocationBps: number;
  isGuardianManaged: boolean;
  requiresApproval: boolean;
  percentIsFixed: boolean;
  sortOrder: number;
}

export interface BucketTemplate {
  templateId: number;
  createdByAccountId: number;
  templateName: string;
  templateDescription: string | null;
  items: BucketTemplateItem[];
  createdAtUtc: string;
}

export interface CreateTemplateInput {
  sessionId: string;
  templateName: string;
  templateDescription?: string;
  items: BucketTemplateItem[];
}

export async function bucketTemplateCreate(
  input: CreateTemplateInput,
): Promise<Result<number, AppError>> {
  try {
    return ok(await invoke<number>('bucket_template_create', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketTemplatesList(
  sessionId: string,
): Promise<Result<BucketTemplate[], AppError>> {
  try {
    return ok(await invoke<BucketTemplate[]>('bucket_templates_list', { input: { sessionId } }));
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketTemplateSaveFromChild(
  sessionId: string,
  childAccountId: number,
  templateName: string,
  description?: string,
): Promise<Result<number, AppError>> {
  try {
    return ok(
      await invoke<number>('bucket_template_save_from_child', {
        input: { sessionId, childAccountId, templateName, templateDescription: description },
      }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketTemplateApply(
  sessionId: string,
  templateId: number,
  childAccountId: number,
): Promise<Result<{ bucketsAdded: number }, AppError>> {
  try {
    return ok(
      await invoke<{ bucketsAdded: number }>('bucket_template_apply', {
        input: { sessionId, templateId, childAccountId },
      }),
    );
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketTemplateDelete(
  sessionId: string,
  templateId: number,
): Promise<Result<void, AppError>> {
  try {
    await invoke('bucket_template_delete', { input: { sessionId, templateId } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

// =============================================================================
// US-REPORT-03 -- Chore completion trends
// =============================================================================

export interface ChoreTrendsWeek {
  weekStart: string;
  completed: number;
  rejected: number;
  missed: number;
  pending: number;
}

export interface ChoreTrendsResult {
  weeks: ChoreTrendsWeek[];
  totalCompleted: number;
  totalRejected: number;
  totalMissed: number;
  completionRatePct: number;
}

export async function reportChoreTrends(input: {
  sessionId: string;
  childAccountId?: number;
  fromDate?: string;
  toDate?: string;
}): Promise<Result<ChoreTrendsResult, AppError>> {
  try {
    return ok(await invoke<ChoreTrendsResult>('report_chore_trends', { input }));
  } catch (e) {
    return err(toAppError(e));
  }
}

// US-BUCKET-19 -- Atomic split update (also used for inconsistency recovery)
export interface BucketSplitEntry {
  bucketId: number;
  allocationBps: number;
}

export interface BucketRebalanceEntry {
  bucketId: number;
  newAllocationBps: number;
}

export async function bucketAutoRebalance(
  sessionId: string,
  childAccountId: number,
): Promise<Result<BucketRebalanceEntry[], AppError>> {
  try {
    const value = await invoke<BucketRebalanceEntry[]>('bucket_auto_rebalance', {
      sessionId,
      childAccountId,
    });
    return { ok: true, value };
  } catch (e) {
    return { ok: false, error: e as AppError };
  }
}

export interface BucketPriorityEntry {
  bucketId: number;
  priority: number;
}

export async function bucketUpdatePriorities(
  sessionId: string,
  childAccountId: number,
  priorities: BucketPriorityEntry[],
): Promise<Result<void, AppError>> {
  try {
    await invoke('bucket_update_priorities', { sessionId, childAccountId, priorities });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}

export async function bucketUpdateSplits(
  sessionId: string,
  childAccountId: number,
  splits: BucketSplitEntry[],
): Promise<Result<void, AppError>> {
  try {
    await invoke('bucket_update_splits', { input: { sessionId, childAccountId, splits } });
    return ok(undefined);
  } catch (e) {
    return err(toAppError(e));
  }
}
